import React, { useCallback, useMemo, useState, useRef, memo, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BCC_TIME_SLOTS, campaignCreationContext, MIN_BULK_CAMPAIGN_BUGET_LIMIT } from "@ts/constants";
import { Form } from "antd";
import { updateCollection, addCluster, removeCluster, enableAllProductsInCluster, restoreClusterPrioritiesToOriginal, restoreClusterVariantsToOriginal } from "@/features/clustersSlice";
import { resetClusterPriorityState, resetProductPriorities, resetAllModalVariantSelections, resetClusterVariantSelections, resetProductVariantSelections } from "@/components/widgets/WidgetCellRenderers";
import { createBulkCampaigns, publishBulkCampaigns, handleSaveCluster as saveCluster, generateCampaignName, extractCityNames, getCohortDisplayLabel, getKeywordTypesForName, getCurrentCollection } from "@ts/utils";
import store from "@/store";
import { showToast } from "@ts/modals";
import { listClusterTargets } from "@/ts/retrieve-data";
import { BCC_CAMPAIGN_TYPES, BCC_MODAL_TYPES, BCC_TARGET_TYPES, CLUSTER_ACCORDION_TYPES, CREATE_CAMPAIGN_HANDLER_ACTIONS, IBCCAsset, IBCCProductSkuDetail, ICampaignCreationContext, ICategoryTarget, INegativeKeywordTarget, KeywordTypeValues, KWD_BID_TYPES, IBCCCollectionState, IBCCCollectionAction, IKeywordCohort, IBCCKeyword } from "@/ts/common-types";
import { updateCampaignCreationDisplay } from "@/features/campaignCreationSlice";
import { useGetModulesData } from "@/hooks/AllCustomHooks";

interface Props {
    children: React.ReactNode;
}

const CreateCampaignContext: React.FC<Props> = memo((props) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const clusters = useSelector(
        (state: any) => state.clusters?.clusters || []
    );
    const [activeTab, setActiveTab] = useState("clusters");
    const [searchValue, setSearchValue] = useState("");
    const [filteredClusters, setFilteredClusters] = useState<any[]>([]);
    const [productGroups, setProductGroups] = useState<any[]>([]);
    const [baseKeywordsByCluster, setBaseKeywordsByCluster] = useState<Record<string, any[]>>({});
    const [baseCategoriesByCluster, setBaseCategoriesByCluster] = useState<Record<string, ICategoryTarget[]>>({});
    
    // Reducer for managing collection-related state
    const collectionReducer = (state: IBCCCollectionState, action: IBCCCollectionAction): IBCCCollectionState => {
        switch (action.type) {
            case 'CLEAR_COLLECTION': {
                const { clusterId, collectionId } = action.payload;
                const clearCollectionFromMap = <T,>(map: Record<string, Record<string, T>>) => {
                    const clusterMap = map[clusterId] ? { ...map[clusterId] } : {};
                    delete clusterMap[collectionId];
                    return { ...map, [clusterId]: clusterMap };
                };

                return {
                    overridesByCollection: clearCollectionFromMap(state.overridesByCollection),
                    selectedKeywordIdsByCollection: clearCollectionFromMap(state.selectedKeywordIdsByCollection),
                    negativeKeywordsByCollection: clearCollectionFromMap(state.negativeKeywordsByCollection),
                    categoriesOverridesByCollection: clearCollectionFromMap(state.categoriesOverridesByCollection),
                    selectedCategoryIdsByCollection: clearCollectionFromMap(state.selectedCategoryIdsByCollection),
                };
            }
            case 'SET_OVERRIDES': {
                const { clusterId, collectionId, overrides } = action.payload;
                const clusterMap = state.overridesByCollection[clusterId] ? { ...state.overridesByCollection[clusterId] } : {};
                clusterMap[collectionId] = overrides;
                return {
                    ...state,
                    overridesByCollection: { ...state.overridesByCollection, [clusterId]: clusterMap },
                };
            }
            case 'SET_SELECTED_KEYWORD_IDS': {
                const { clusterId, collectionId, ids } = action.payload;
                const clusterMap = state.selectedKeywordIdsByCollection[clusterId] ? { ...state.selectedKeywordIdsByCollection[clusterId] } : {};
                clusterMap[collectionId] = ids;
                return {
                    ...state,
                    selectedKeywordIdsByCollection: { ...state.selectedKeywordIdsByCollection, [clusterId]: clusterMap },
                };
            }
            case 'SET_NEGATIVE_KEYWORDS': {
                const { clusterId, collectionId, keywords } = action.payload;
                const clusterMap = state.negativeKeywordsByCollection[clusterId] ? { ...state.negativeKeywordsByCollection[clusterId] } : {};
                clusterMap[collectionId] = keywords;
                return {
                    ...state,
                    negativeKeywordsByCollection: { ...state.negativeKeywordsByCollection, [clusterId]: clusterMap },
                };
            }
            case 'SET_CATEGORIES_OVERRIDES': {
                const { clusterId, collectionId, overrides } = action.payload;
                const clusterMap = state.categoriesOverridesByCollection[clusterId] ? { ...state.categoriesOverridesByCollection[clusterId] } : {};
                clusterMap[collectionId] = overrides;
                return {
                    ...state,
                    categoriesOverridesByCollection: { ...state.categoriesOverridesByCollection, [clusterId]: clusterMap },
                };
            }
            case 'SET_SELECTED_CATEGORY_IDS': {
                const { clusterId, collectionId, ids } = action.payload;
                const clusterMap = state.selectedCategoryIdsByCollection[clusterId] ? { ...state.selectedCategoryIdsByCollection[clusterId] } : {};
                clusterMap[collectionId] = ids;
                return {
                    ...state,
                    selectedCategoryIdsByCollection: { ...state.selectedCategoryIdsByCollection, [clusterId]: clusterMap },
                };
            }
            default:
                return state;
        }
    };

    const [collectionState, dispatchCollection] = useReducer(collectionReducer, {
        overridesByCollection: {},
        selectedKeywordIdsByCollection: {},
        negativeKeywordsByCollection: {},
        categoriesOverridesByCollection: {},
        selectedCategoryIdsByCollection: {},
    });

    // Use a ref to always have access to the latest state
    const collectionStateRef = useRef(collectionState);
    collectionStateRef.current = collectionState;

    // Extract individual state values for backward compatibility
    const overridesByCollection = collectionState.overridesByCollection;
    const selectedKeywordIdsByCollection = collectionState.selectedKeywordIdsByCollection;
    const negativeKeywordsByCollection = collectionState.negativeKeywordsByCollection;
    const categoriesOverridesByCollection = collectionState.categoriesOverridesByCollection;
    const selectedCategoryIdsByCollection = collectionState.selectedCategoryIdsByCollection;

    // Helper functions to update individual states (for backward compatibility with existing code)
    // These accept updater functions and dispatch to the reducer
    const setOverridesByCollection = useCallback((updater: (prev: Record<string, Record<string, Record<string, any>>>) => Record<string, Record<string, Record<string, any>>>) => {
        // Use ref to get the latest state instead of stale closure
        const newState = updater(collectionStateRef.current.overridesByCollection);
        // Update all changed collections
        Object.keys(newState).forEach(clusterId => {
            Object.keys(newState[clusterId] || {}).forEach(collectionId => {
                dispatchCollection({
                    type: 'SET_OVERRIDES',
                    payload: { clusterId, collectionId, overrides: newState[clusterId][collectionId] },
                });
            });
        });
    }, []);

    const setSelectedKeywordIdsByCollection = useCallback((updater: (prev: Record<string, Record<string, string[]>>) => Record<string, Record<string, string[]>>) => {
        const newState = updater(collectionState.selectedKeywordIdsByCollection);
        Object.keys(newState).forEach(clusterId => {
            Object.keys(newState[clusterId] || {}).forEach(collectionId => {
                dispatchCollection({
                    type: 'SET_SELECTED_KEYWORD_IDS',
                    payload: { clusterId, collectionId, ids: newState[clusterId][collectionId] },
                });
            });
        });
    }, [collectionState.selectedKeywordIdsByCollection]);

    const setNegativeKeywordsByCollection = useCallback((updater: (prev: Record<string, Record<string, INegativeKeywordTarget[]>>) => Record<string, Record<string, INegativeKeywordTarget[]>>) => {
        const newState = updater(collectionState.negativeKeywordsByCollection);
        Object.keys(newState).forEach(clusterId => {
            Object.keys(newState[clusterId] || {}).forEach(collectionId => {
                dispatchCollection({
                    type: 'SET_NEGATIVE_KEYWORDS',
                    payload: { clusterId, collectionId, keywords: newState[clusterId][collectionId] },
                });
            });
        });
    }, [collectionState.negativeKeywordsByCollection]);

    const setCategoriesOverridesByCollection = useCallback((updater: (prev: Record<string, Record<string, Record<string, Partial<ICategoryTarget>>>>) => Record<string, Record<string, Record<string, Partial<ICategoryTarget>>>>) => {
        // Use ref to get the latest state instead of stale closure
        const newState = updater(collectionStateRef.current.categoriesOverridesByCollection);
        Object.keys(newState).forEach(clusterId => {
            Object.keys(newState[clusterId] || {}).forEach(collectionId => {
                dispatchCollection({
                    type: 'SET_CATEGORIES_OVERRIDES',
                    payload: { clusterId, collectionId, overrides: newState[clusterId][collectionId] },
                });
            });
        });
    }, []);

    const setSelectedCategoryIdsByCollection = useCallback((updater: (prev: Record<string, Record<string, string[]>>) => Record<string, Record<string, string[]>>) => {
        const newState = updater(collectionState.selectedCategoryIdsByCollection);
        Object.keys(newState).forEach(clusterId => {
            Object.keys(newState[clusterId] || {}).forEach(collectionId => {
                dispatchCollection({
                    type: 'SET_SELECTED_CATEGORY_IDS',
                    payload: { clusterId, collectionId, ids: newState[clusterId][collectionId] },
                });
            });
        });
    }, [collectionState.selectedCategoryIdsByCollection]);
    
    const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
    const [expandedClusters, setExpandedClusters] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [isSavingCluster, setIsSavingCluster] = useState(false);
    const [isPublishingCampaigns, setIsPublishingCampaigns] = useState(false);
    // Track which clusters have had their keywords/categories fetched
    const [fetchedClustersTargets, setFetchedClustersTargets] = useState<Record<string, boolean>>({});
    // Track loading state per cluster
    const [loadingClustersTargets, setLoadingClustersTargets] = useState<Record<string, boolean>>({});
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [clusterName, setClusterName] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState<BCC_MODAL_TYPES>(BCC_MODAL_TYPES.CLUSTER);
    const [modalParams, setModalParams] = useState<any>({
        clusterId: "",
        selectedKwTypes: []
    });
    
    const getKeywordKey = useCallback((kw: any) => {
        const k = kw?.keyword ?? '';
        const t = kw?.keyword_type ?? '';
        const cId = kw?.clusterId ?? modalParams?.clusterId ?? '';
        const colId = kw?.id ?? modalParams?.collectionId ?? '';
        return `${cId}||${colId}||${k}||${t}`;
    }, [modalParams]);

    const getCategoryKey = useCallback((cat: any) => {
        const categoryId = cat?.category_id ?? '';
        const cId = cat?.clusterId ?? modalParams?.clusterId ?? '';
        const colId = cat?.id ?? modalParams?.collectionId ?? '';
        return `${cId}||${colId}||${categoryId}`;
    }, [modalParams]);

    const [selectedClusters, setSelectedClusters] = useState<string[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [citiesList, setCitiesList] = useState<any[]>([]);
    const [stepMeta] = useState([
        {
            title: "Select Products",
            description: "Choose products",
            accordionType: CLUSTER_ACCORDION_TYPES.PRODUCT
        },
        {
            title: "Collections",
            description: "Create campaign Collections",
            accordionType: CLUSTER_ACCORDION_TYPES.CLUSTER
        },
        {
            title: "Review & Generate",
            description: "Review campaign Collections",
            accordionType: CLUSTER_ACCORDION_TYPES.CLUSTER
        },
    ]);

    const channelOptions = useGetModulesData({ type: "channel-options" });
    const minCampaignBudget = useMemo(() => {
        return channelOptions?.minCampaignBudget || MIN_BULK_CAMPAIGN_BUGET_LIMIT;
    }, [channelOptions.minCampaignBudget]);

    const activeStep = useMemo(() => {
        return stepMeta[currentStep];
    }, [currentStep, stepMeta]) as any;

    const handleModalCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        // Reset isActiveForSession state for selected products before clearing selection
        selectedProducts.forEach((product: any) => {
            if (product?.skuDetails && Array.isArray(product.skuDetails)) {
                product.skuDetails.forEach((skuDetail: any) => {
                    skuDetail.isActiveForSession = skuDetail.isActive !== undefined ? skuDetail.isActive : false;
                });
            }
        });
        setSelectedProducts([]);
        setClusterName("");
        // Reset any locally assigned priorities and variant selections in the modal context
        resetClusterPriorityState('default');
        resetAllModalVariantSelections();
        prevSelectedProductsRef.current = [];
        
        // Clear keyword selections for the current collection when modal is cancelled
        const { clusterId, collectionId } = modalParams || {};
        if (clusterId && collectionId) {
            dispatchCollection({
                type: 'CLEAR_COLLECTION',
                payload: { clusterId, collectionId },
            });
        }
    };

    const prevSelectedProductsRef = useRef<any[]>([]);
    const handleProductSelection = useCallback((selectedRows: any[]) => {
        setSelectedProducts(selectedRows);
    }, []);

    // When in modal step 0 (CampaignCreationModal with product selection),
    // reset priority for products that were deselected
    React.useEffect(() => {
        if (!isModalVisible || currentStep !== 0) return;
        const prev = prevSelectedProductsRef.current || [];
        const prevIds = new Set(prev.map((r: any) => String(r?.id)));
        const currIds = new Set((selectedProducts || []).map((r: any) => String(r?.id)));

        const deselectedIds: string[] = [];
        prevIds.forEach((id) => {
            if (!currIds.has(id)) {
                deselectedIds.push(id);
            }
        });

        if (deselectedIds.length > 0) {
            // Use 'default' cluster namespace for modal priority state and variant selections
            resetProductPriorities('default', deselectedIds);
            resetProductVariantSelections('default', deselectedIds);
        }

        prevSelectedProductsRef.current = selectedProducts || [];
    }, [selectedProducts, isModalVisible, currentStep]);

    // Handler for table keyword selection from table widget
    const handleKeywordSelection = useCallback((selectedRows: IBCCKeyword[]) => {
        const { clusterId, collectionId, selectedKwTypes } = modalParams || {};
        if (!clusterId || !collectionId) return;

        // Get previously selected keyword IDs to detect deselected keywords
        const previousSelectedIds = selectedKeywordIdsByCollection?.[clusterId]?.[collectionId] || [];
        const newSelectedIds = selectedRows.map((r: IBCCKeyword) => getKeywordKey(r));
        const newSelectedIdSet = new Set(newSelectedIds);

        // Compute currently visible keywords (filtered by selectedKwTypes) directly from base data
        const base = baseKeywordsByCluster?.[clusterId] || [];
        const overrides = overridesByCollection?.[clusterId]?.[collectionId] || {};
        
        // Merge base with overrides
        const mergedKeywords = base.map((kw: IBCCKeyword) => ({
            ...kw,
            ...(overrides[getKeywordKey(kw)] || {}),
            key: getKeywordKey(kw),
            clusterId,
            id: collectionId,
        }));
        
        // Filter by selected keyword types (same logic as currentKeywords)
        const visibleKeywords = (selectedKwTypes && selectedKwTypes.length > 0)
            ? mergedKeywords.filter((kw: IBCCKeyword) => 
                kw.keyword_type === KeywordTypeValues.CUSTOM || 
                kw.isCustom || 
                selectedKwTypes.includes(kw.keyword_type)
            )
            : mergedKeywords;
        
        // Get keys of visible keywords
        const visibleKeywordKeys = new Set(
            visibleKeywords.map((kw: IBCCKeyword) => getKeywordKey(kw))
        );

        // Merge selection: 
        const mergedSelectedIds = [
            ...newSelectedIds,
            ...previousSelectedIds.filter((id: string) => !visibleKeywordKeys.has(id))
        ];

        // Remove duplicates
        const finalSelectedIds = Array.from(new Set(mergedSelectedIds));

        // Find deselected keywords (were selected before, but not in final selection)
        const deselectedIds = previousSelectedIds.filter((id: string) => 
            visibleKeywordKeys.has(id) && !newSelectedIdSet.has(id)
        );

        // Clear overrides for deselected keywords so bid values don't persist
        if (deselectedIds.length > 0) {
            setOverridesByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                const collOverrides = clusterMap[collectionId] ? { ...clusterMap[collectionId] } : {};
                
                // Remove overrides for each deselected keyword
                deselectedIds.forEach((keywordKey: string) => {
                    delete collOverrides[keywordKey];
                });
                
                clusterMap[collectionId] = collOverrides;
                return { ...prev, [clusterId]: clusterMap };
            });
        }

        // Store merged table keyword IDs (preserving filtered-out selections)
        setSelectedKeywordIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            clusterMap[collectionId] = finalSelectedIds;
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [modalParams, getKeywordKey, selectedKeywordIdsByCollection, baseKeywordsByCluster, overridesByCollection]);

    // Handler for table category selection from table widget
    const handleCategorySelection = useCallback((selectedRows: ICategoryTarget[]) => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;

        // Store table category IDs
        setSelectedCategoryIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            clusterMap[collectionId] = selectedRows.map((r: ICategoryTarget) => getCategoryKey(r));
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [modalParams, getCategoryKey]);

    // Handler specifically for negative keywords - doesn't affect table keyword selection
    const handleNegativeKeywordSelection = useCallback((negativeKeywords: INegativeKeywordTarget[]) => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;

        // Only update negative keywords, leave table keywords untouched
        setNegativeKeywordsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            clusterMap[collectionId] = negativeKeywords.map((r) => ({
                keyword: r.keyword,
                keyword_type: r.keyword_type
            }));
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [modalParams]);

    const handleClusterSelection = useCallback(
        (clusterId: string) => {
            setSelectedClusters((prev) => {
                if (prev.includes(clusterId)) {
                    // Deselecting cluster
                    const newSelection = prev.filter((id) => id !== clusterId);
                    return newSelection;
                } else {
                    // Selecting cluster
                    const newSelection = [...prev, clusterId];
                    return newSelection;
                }
            });
        },
        [clusters]
    );

    const handleClusterDeselection = useCallback(
        (clusterId: string) => {
            setSelectedClusters((prev) => {
                const newSelection = prev.filter((id) => id !== clusterId);
                return newSelection;
            });

            // Enable all products in the deselected cluster (set disabled to false)
            dispatch(enableAllProductsInCluster(clusterId));

            // Restore priorities and variants in Redux state to original DB values
            dispatch(restoreClusterPrioritiesToOriginal(clusterId));
            dispatch(restoreClusterVariantsToOriginal(clusterId));

            // Reset priority and variant state for the deselected cluster so it shows original DB values when re-added
            resetClusterPriorityState(clusterId);
            resetClusterVariantSelections(clusterId);
        },
        [clusters, dispatch]
    );

    const handleProductSelectionInLeftPanel = useCallback(
        (productId: string) => {
            setSelectedProductIds((prev) => {
                if (prev.includes(productId)) {
                    // Deselecting product
                    const newSelection = prev.filter((id) => id !== productId);
                    return newSelection;
                } else {
                    // Selecting product
                    const selectedProduct = productGroups.find(
                        (product: any) => product.id === productId
                    );
                    // Add the product as a cluster in Redux store
                    if (selectedProduct) {
                        // Set isActiveForSession to true for all variants when adding as cluster
                        const productWithActiveVariants = {
                            ...selectedProduct,
                            priority: selectedProduct.priority_value || 0,
                            skuDetails: selectedProduct.skuDetails?.map((skuDetail: IBCCProductSkuDetail) => ({
                                ...skuDetail,
                                isActiveForSession: true
                            })) || []
                        };

                        dispatch(
                            addCluster({
                                clusterId: `##productCluster_${selectedProduct.id}`,
                                name: selectedProduct.title,
                                products: [productWithActiveVariants]
                            })
                        );
                    }
                    
                    const newSelection = [...prev, productId];
                    return newSelection;
                }
            });
        },
        [productGroups, dispatch]
    );

    const handleProductDeselection = useCallback(
        (clusterId: string) => {
            // Extract product ID from cluster ID: ##productCluster_123 -> 123
            const productId = clusterId.replace("##productCluster_", "");

            const deselectedProduct = productGroups.find(
                (product: any) => product.id === productId
            );

            // Remove the product's cluster from Redux store
            if (deselectedProduct) {
                dispatch(restoreClusterVariantsToOriginal(clusterId));

                dispatch(
                    removeCluster(clusterId)
                );
                // Reset priority state and variant selections for the deselected product cluster so it shows original values when re-added
                resetClusterPriorityState(clusterId);
                resetAllModalVariantSelections();
            }

            setSelectedProductIds((prev) => {
                const newSelection = prev.filter((id) => id !== productId);
                return newSelection;
            });
        },
        [productGroups, dispatch]
    );

    const handleSaveCluster = async (shouldAddCluster?: boolean) => {
        await saveCluster(
            clusterName,
            selectedProducts,
            dispatch,
            setIsSavingCluster,
            isSavingCluster,
            productGroups,
            handleModalCancel,
            shouldAddCluster,
            setSelectedClusters,
            selectedClusters
        );
    };

    // Generate campaigns from cohorts and collection data
    const generateCampaignsFromCohorts = useCallback((
        cohorts: IKeywordCohort[],
        cities: string[],
        collectionType: string,
        campaignType: string,
        clusterName: string,
        clusterId: string,
        collectionId: string
    ): any[] => {
        if (!collectionType || cities.length === 0 || cohorts.length === 0) {
            return [];
        }
        if (!collectionType || cities.length === 0 || cohorts.length === 0) {
            return [];
        }

        const timeSlots = BCC_TIME_SLOTS;
        const campaigns: any[] = [];

        if (collectionType === BCC_CAMPAIGN_TYPES.SINGLE) {
            // Single: 1 campaign per cohort with all cities
            for (const cohort of cohorts) {
                const cohortLabel = getCohortDisplayLabel(cohort);
                const cityNames = extractCityNames(cities);
                const keywordTypesForName = getKeywordTypesForName(cohort);
                const campaignName = generateCampaignName(clusterName, keywordTypesForName, cityNames, timeSlots, campaignType);
                
                campaigns.push({
                    id: `campaign-${Date.now()}${Math.floor(Math.random() * 1000)}`.slice(-16),
                    collectionId,
                    clusterId,
                    campaignType,
                    keywordType: cohort.keywordTypes || [],
                    cohortId: cohort.id,
                    cohortLabel,
                    keywords: cohort.keywords || [],
                    negative_keywords: cohort.negativeKeywords || [],
                    categories: cohort.categories || [],
                    city: cities,
                    timeSlot: timeSlots,
                    budget: null as any,
                    type: collectionType,
                    isArchived: false,
                    name: campaignName
                });
            }
        } else {
            // Multiple: cohort x city combinations
            for (const cohort of cohorts) {
                const cohortLabel = getCohortDisplayLabel(cohort);
                const keywordTypesForName = getKeywordTypesForName(cohort);
                
                for (const city of cities) {
                    const cityName = extractCityNames([city]);
                    const campaignName = generateCampaignName(clusterName, keywordTypesForName, cityName, timeSlots, campaignType);

                    campaigns.push({
                        id: `campaign-${Date.now()}${Math.floor(Math.random() * 1000)}`.slice(-16),
                        collectionId,
                        clusterId,
                        campaignType,
                        keywordType: cohort.keywordTypes || [],
                        cohortId: cohort.id,
                        cohortLabel,
                        keywords: cohort.keywords || [],
                        negative_keywords: cohort.negativeKeywords || [],
                        categories: cohort.categories || [],
                        city: [city],
                        timeSlot: timeSlots,
                        budget: null as any,
                        type: collectionType,
                        isArchived: false,
                        name: campaignName
                    });
                }
            }
        }

        return campaigns;
    }, []);

    // Handle cohort deletion
    const handleDeleteCohort = useCallback((
        clusterId: string,
        collectionId: string,
        cohortIdToDelete: string
    ) => {
        const { cluster, collection } = getCurrentCollection(clusterId, collectionId);
        if (!collection?.keywordCohorts) return;

        const updatedCohorts = collection.keywordCohorts.filter(
            (cohort: IKeywordCohort) => cohort.id !== cohortIdToDelete
        );
        
        const allKeywords = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.keywords || []);
        const allNegativeKeywords = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.negativeKeywords || []);
        const allCategories = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.categories || []);
        const allKeywordTypes = [...new Set(updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.keywordTypes || []))];
        
        const citiesRaw = collection?.city || [];
        const cities: string[] = Array.isArray(citiesRaw) ? citiesRaw : (citiesRaw ? [citiesRaw] : []);
        const campaignType = collection?.campaignType;
        const collectionType = collection?.type;
        const clusterName = cluster?.name || '';

        const updatedCampaigns = generateCampaignsFromCohorts(
            updatedCohorts,
            cities,
            collectionType || '',
            campaignType || '',
            clusterName,
            clusterId,
            collectionId
        );
        
        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    keywordCohorts: updatedCohorts,
                    keywords: allKeywords,
                    negative_keywords: allNegativeKeywords,
                    categories: allCategories,
                    keywordType: allKeywordTypes,
                    campaigns: updatedCampaigns,
                }
            })
        );
    }, [generateCampaignsFromCohorts, dispatch]);

    // Handle keyword field updates (bid fields)
    const handleKeywordFieldUpdate = useCallback((
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record: any
    ) => {
        const key = getKeywordKey(record);
        setOverridesByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            const collOverrides = clusterMap[collectionId] ? { ...clusterMap[collectionId] } : {};
            const existing = collOverrides[key] || {};
            collOverrides[key] = { ...existing, [field]: value };
            clusterMap[collectionId] = collOverrides;
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [getKeywordKey, setOverridesByCollection]);

    // Handle batched keyword field updates (for multiple keywords at once)
    const handleBatchKeywordFieldUpdates = useCallback((
        updates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }>
    ) => {
        if (!updates.length) return;
        
        setOverridesByCollection((prev) => {
            const newState = { ...prev };
            
            updates.forEach(({ clusterId, collectionId, field, value, record }) => {
                const key = getKeywordKey(record);
                if (!newState[clusterId]) {
                    newState[clusterId] = {};
                }
                if (!newState[clusterId][collectionId]) {
                    newState[clusterId][collectionId] = {};
                }
                const existing = newState[clusterId][collectionId][key] || {};
                newState[clusterId][collectionId][key] = { ...existing, [field]: value };
            });
            
            return newState;
        });
    }, [getKeywordKey, setOverridesByCollection]);

    // Handle category field updates
    const handleCategoryFieldUpdate = useCallback((
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record: any
    ) => {
        const key = getCategoryKey(record);
        setCategoriesOverridesByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            const collOverrides = clusterMap[collectionId] ? { ...clusterMap[collectionId] } : {};
            const existing = collOverrides[key] || {};
            collOverrides[key] = { ...existing, [field]: value };
            clusterMap[collectionId] = collOverrides;
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [getCategoryKey, setCategoriesOverridesByCollection]);

    // Handle batched category field updates (for multiple categories at once)
    const handleBatchCategoryFieldUpdates = useCallback((
        updates: Array<{ clusterId: string; collectionId: string; field: string; value: any; record: any }>
    ) => {
        if (!updates.length) return;
        
        setCategoriesOverridesByCollection((prev) => {
            const newState = { ...prev };
            
            updates.forEach(({ clusterId, collectionId, field, value, record }) => {
                const key = getCategoryKey(record);
                if (!newState[clusterId]) {
                    newState[clusterId] = {};
                }
                if (!newState[clusterId][collectionId]) {
                    newState[clusterId][collectionId] = {};
                }
                const existing = newState[clusterId][collectionId][key] || {};
                newState[clusterId][collectionId][key] = { ...existing, [field]: value };
            });
            
            return newState;
        });
    }, [getCategoryKey, setCategoriesOverridesByCollection]);

    // Generate campaign combinations from collection with updated field
    const createCampaignCombinationsFromCollection = useCallback((
        clusterId: string,
        collectionId: string,
        field: string,
        value: any
    ) => {
        const { cluster, collection } = getCurrentCollection(clusterId, collectionId);
        if (!collection || !cluster) {
            return [];
        }

        const updatedCollection = {
            ...collection,
            [field]: value
        };

        if (!updatedCollection.type) {
            return [];
        }

        const cities = Array.isArray(updatedCollection.city) ? updatedCollection.city : (updatedCollection.city ? [updatedCollection.city] : []);
        const cohorts = updatedCollection.keywordCohorts || [];

        return generateCampaignsFromCohorts(
            cohorts,
            cities,
            updatedCollection.type,
            updatedCollection.campaignType,
            cluster.name,
            clusterId,
            collectionId
        );
    }, [generateCampaignsFromCohorts]);

    // Handle campaign combinator field updates (fields that affect campaign generation)
    const handleCampaignCombinatorFieldUpdate = useCallback((
        clusterId: string,
        collectionId: string,
        field: string,
        value: any
    ) => {
        const currentCollection = getCurrentCollection(clusterId, collectionId);
        const combinations = createCampaignCombinationsFromCollection(clusterId, collectionId, field, value);
        
        // If type is being changed to "single" and there's a budget, assign it to campaigns
        let updatedCampaigns = combinations || [];
        if (field === 'type' && value === BCC_CAMPAIGN_TYPES.SINGLE && currentCollection?.collection?.budget) {
            const budgetValue = Number(currentCollection.collection.budget) || 0;
            if (budgetValue > 0) {
                updatedCampaigns = updatedCampaigns.map((campaign: any) => ({
                    ...campaign,
                    budget: budgetValue
                }));
            }
        }
        
        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    [field]: value,
                    campaigns: updatedCampaigns,
                }
            })
        );
    }, [createCampaignCombinationsFromCollection, dispatch]);

    // Handle campaign target field updates (keywords, negative keywords, categories)
    const handleCampaignTargetFieldUpdate = useCallback((
        clusterId: string,
        collectionId: string,
        field: string,
        value: any
    ) => {
        const { collection } = getCurrentCollection(clusterId, collectionId);
        if (!collection?.campaigns?.length) return;

        const updatedCampaigns = collection.campaigns.map((campaign: any) => {
            if (field === BCC_TARGET_TYPES.KEYWORDS) {
                const keywordType = campaign.keywordType?.[0];
                const matchingKeywords = value?.filter((kwd: any) => 
                    kwd?.keyword_type === keywordType
                ) || [];
                return { ...campaign, keywords: matchingKeywords };
            } else if (field === BCC_TARGET_TYPES.NEGATIVE_KEYWORDS) {
                return { ...campaign, negative_keywords: value };
            } else if (field === BCC_TARGET_TYPES.CATEGORIES) {
                return { ...campaign, categories: value };
            }
            return campaign;
        });

        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    [field]: value,
                    campaigns: updatedCampaigns,
                }
            })
        );
    }, [dispatch]);

    // Handle budget updates for single campaign type
    const handleBudgetUpdate = useCallback((
        clusterId: string,
        collectionId: string,
        value: any
    ) => {
        const { collection } = getCurrentCollection(clusterId, collectionId);
        if (collection?.type !== BCC_CAMPAIGN_TYPES.SINGLE || !collection.campaigns?.length) return;

        const updatedCampaigns = collection.campaigns.map((campaign: any) => ({
            ...campaign,
            budget: value
        }));

        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    budget: value,
                    campaigns: updatedCampaigns,
                }
            })
        );
    }, [dispatch]);

    
    const handleFieldChange = (
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record?: any
    ) => {
        // Route to appropriate handler based on field type
        if (field === 'deleteCohort') {
            handleDeleteCohort(clusterId, collectionId, value);
            return;
        }

        const keywordFields = [KWD_BID_TYPES.EXACT_MATCH_BID, KWD_BID_TYPES.BID_BOOSTER, KWD_BID_TYPES.SMART_MATCH_BID];
        if (keywordFields.includes(field as KWD_BID_TYPES) && record?.keyword && record?.keyword_type) {
            handleKeywordFieldUpdate(clusterId, collectionId, field, value, record);
            return;
        }

        if (record?.category_id) {
            handleCategoryFieldUpdate(clusterId, collectionId, field, value, record);
            return;
        }

        const campaignCombinatorFields = ['campaignType', 'keywordType', 'city', 'timeSlot', 'type'];
        if (campaignCombinatorFields.includes(field)) {
            handleCampaignCombinatorFieldUpdate(clusterId, collectionId, field, value);
            return;
        }

        const campaignTargetFields = [BCC_TARGET_TYPES.KEYWORDS, BCC_TARGET_TYPES.NEGATIVE_KEYWORDS, BCC_TARGET_TYPES.CATEGORIES];
        if (campaignTargetFields.includes(field as BCC_TARGET_TYPES)) {
            handleCampaignTargetFieldUpdate(clusterId, collectionId, field, value);
            return;
        }

        if (field === 'budget') {
            // For single campaign type with campaigns, update both collection and campaign budgets
            const { collection } = getCurrentCollection(clusterId, collectionId);
            if (collection?.type === BCC_CAMPAIGN_TYPES.SINGLE && collection.campaigns?.length) {
                handleBudgetUpdate(clusterId, collectionId, value);
                return;
            }
        }

        // Default: simple field update
        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: { [field]: value }
            })
        );
    };

    const handleCampaignNameChange = (
        clusterId: string,
        collectionId: string,
        campaignId: string,
        newName: string
    ) => {
        const currentState = store.getState();
        const allClusters = currentState?.clusters?.clusters || [];
        const cluster = allClusters.find((cluster: any) => cluster.id === clusterId);
        const collection = cluster?.collections.find((collection: any) => collection.id === collectionId);
        
        if (!collection) return;

        // Find and update the specific campaign
        const updatedCampaigns = (collection.campaigns || []).map((campaign: any) => {
            if (campaign.id === campaignId) {
                return { ...campaign, name: newName };
            }
            return campaign;
        });

        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    campaigns: updatedCampaigns
                }
            })
        );
    };

    const fetchClusterTargets = useCallback(async (clusterId: string) => {
        // If already fetched or currently loading, skip
        if (fetchedClustersTargets[clusterId] || loadingClustersTargets[clusterId]) {
            return;
        }

        setLoadingClustersTargets((prev) => ({ ...prev, [clusterId]: true }));

        try {
            const cluster = clusters.find((cl: any) => cl.id === clusterId);
            const skuList = cluster?.products?.map((product: any) => product.sku?.toString()) || [];

            // Fetch keywords and categories
            const res = await listClusterTargets({ skuList });

            const apiKeywords = res?.keywords?.map((row: any) => ({
                ...row,
                exact_match_bid: row?.exact_match_bid ?? null,
                smart_match_bid: row?.smart_match_bid ?? null,
                bid_booster: row?.bid_booster ?? null,
            })) || [];

            const apiCategories = res?.categories || [];

            setBaseKeywordsByCluster((prev) => {
                return { ...prev, [clusterId]: apiKeywords };
            });

            setBaseCategoriesByCluster((prev) => {
                return { ...prev, [clusterId]: apiCategories };
            });
            
            // Mark this cluster as fetched
            setFetchedClustersTargets((prev) => ({ ...prev, [clusterId]: true }));
        } catch (error) {
            console.error('Error fetching targets:', error);
        } finally {
            setLoadingClustersTargets((prev) => ({ ...prev, [clusterId]: false }));
        }
    }, [clusters, fetchedClustersTargets, loadingClustersTargets]);

    const handleAddKeywords = async (clusterId: string, collectionId: string, selectedKwTypes: string[]) => {
        setModalParams({
            clusterId,
            collectionId,
            selectedKwTypes: selectedKwTypes,
            editingCohortId: null // New cohort, not editing
        });
        setModalType(BCC_MODAL_TYPES.KEYWORDS);
        setIsModalVisible(true);
        try {
            // Ensure maps exist for collection; no need to clone large arrays
            setOverridesByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                if (!clusterMap[collectionId]) clusterMap[collectionId] = {};
                return { ...prev, [clusterId]: clusterMap };
            });

            // Ensure category overrides map exists for collection
            setCategoriesOverridesByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                if (!clusterMap[collectionId]) clusterMap[collectionId] = {};
                return { ...prev, [clusterId]: clusterMap };
            });
            
            // For cohort workflow: Start with fresh selection (no preselection)
            // Each cohort is a new selection, so we clear any existing selections
            setSelectedKeywordIdsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = []; // Start fresh
                return { ...prev, [clusterId]: clusterMap };
            });

            // Clear category selections for fresh start
            setSelectedCategoryIdsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = []; // Start fresh
                return { ...prev, [clusterId]: clusterMap };
            });

            // Clear negative keywords for fresh start
            setNegativeKeywordsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = []; // Start fresh
                return { ...prev, [clusterId]: clusterMap };
            });
        } catch (error) {
            console.error('Error in handleAddKeywords:', error);
        }
    };

    // Handler for editing an existing cohort
    const handleEditCohort = async (clusterId: string, collectionId: string, cohortId: string) => {
        // Find the cohort to edit
        const collection = clusters
            .find((cl: any) => cl.id === clusterId)?.collections
            ?.find((c: any) => c.id === collectionId);
        
        const cohort = collection?.keywordCohorts?.find((c: IKeywordCohort) => c.id === cohortId);
        if (!cohort) return;

        // Determine keyword types from cohort
        const selectedKwTypes = cohort.keywordTypes?.length > 0 
            ? cohort.keywordTypes 
            : [KeywordTypeValues.BRANDED, KeywordTypeValues.GENERIC, KeywordTypeValues.COMPETITION];

        setModalParams({
            clusterId,
            collectionId,
            selectedKwTypes,
            editingCohortId: cohortId // Mark as editing this cohort
        });
        setModalType(BCC_MODAL_TYPES.KEYWORDS);
        setIsModalVisible(true);

        try {
            // Pre-populate with cohort's existing keywords and their bid overrides
            const cohortKeywords = cohort.keywords || [];
            
            // Build keyword overrides from the cohort's keywords (to preserve bids)
            const keywordOverrides: Record<string, any> = {};
            cohortKeywords.forEach((kw: IBCCKeyword) => {
                const key = getKeywordKey({ ...kw, clusterId, id: collectionId });
                keywordOverrides[key] = {
                    exact_match_bid: kw.exact_match_bid,
                    smart_match_bid: kw.smart_match_bid,
                    bid_booster: kw.bid_booster,
                };
            });
            
            setOverridesByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = keywordOverrides;
                return { ...prev, [clusterId]: clusterMap };
            });

            // Pre-populate with cohort's existing categories and their bid overrides
            const cohortCategories = cohort.categories || [];
            
            // Build category overrides from the cohort's categories (to preserve bids)
            const categoryOverrides: Record<string, any> = {};
            cohortCategories.forEach((cat: any) => {
                const key = getCategoryKey({ ...cat, clusterId, id: collectionId });
                categoryOverrides[key] = {
                    cpm_bid: cat.cpm_bid,
                };
            });
            
            setCategoriesOverridesByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = categoryOverrides;
                return { ...prev, [clusterId]: clusterMap };
            });

            // Set selected keyword IDs
            setSelectedKeywordIdsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = cohortKeywords.map((kw: any) => 
                    getKeywordKey({ ...kw, clusterId, id: collectionId })
                );
                return { ...prev, [clusterId]: clusterMap };
            });

            // Set selected category IDs
            setSelectedCategoryIdsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = cohortCategories.map((cat: any) => 
                    getCategoryKey({ ...cat, clusterId, id: collectionId })
                );
                return { ...prev, [clusterId]: clusterMap };
            });

            // Pre-populate with cohort's existing negative keywords
            const cohortNegativeKeywords = cohort.negativeKeywords || [];
            setNegativeKeywordsByCollection((prev) => {
                const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
                clusterMap[collectionId] = cohortNegativeKeywords.map((kw: any) => ({
                    keyword: kw.keyword,
                    keyword_type: kw.keyword_type
                }));
                return { ...prev, [clusterId]: clusterMap };
            });
        } catch (error) {
            console.error('Error in handleEditCohort:', error);
        }
    };

    const handleAddCustomKeyword = useCallback((customKeyword: any) => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;

        // Add custom keyword to the base keywords for this cluster
        setBaseKeywordsByCluster((prev) => {
            const currentKeywords = prev[clusterId] || [];
            // Add to the beginning so it appears at the top
            return { ...prev, [clusterId]: [customKeyword, ...currentKeywords] };
        });

        // Automatically select the custom keyword
        setSelectedKeywordIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            const currentSelectedIds = clusterMap[collectionId] || [];
            const customKeywordKey = getKeywordKey(customKeyword);
            // Add the custom keyword to the beginning of selected keywords
            clusterMap[collectionId] = [customKeywordKey, ...currentSelectedIds];
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [modalParams, getKeywordKey]);

    const handleAddAssets = (clusterId: string, collectionId: string) => {
        setModalType(BCC_MODAL_TYPES.ASSETS);

        setModalParams({
            clusterId,
            collectionId
        });
        setIsModalVisible(true);
    };


    const handleSaveKeywords = () => {
        const { clusterId, collectionId, selectedKwTypes, editingCohortId } = modalParams;
        if (!clusterId || !collectionId) return;
        
        // Get current collection to check existing cohorts
        const currentState = store.getState();
        const allClusters = currentState?.clusters?.clusters || [];
        const currentCluster = allClusters.find((c: any) => c.id === clusterId);
        const currentCollection = currentCluster?.collections.find((c: any) => c.id === collectionId);
        const existingCohorts = currentCollection?.keywordCohorts || [];
        
        // Check cohort limit only when creating new (not editing)
        if (!editingCohortId && existingCohorts.length >= 5) {
            setIsModalVisible(false);
            return;
        }
        
        // Get selected table keywords with overrides
        const selectedIds = selectedKeywordIdsByCollection?.[clusterId]?.[collectionId] || [];
        const base = baseKeywordsByCluster?.[clusterId] || [];
        const overrides = overridesByCollection?.[clusterId]?.[collectionId] || {};
        const merged = base.map((kw: any) => ({ ...kw, ...(overrides[getKeywordKey(kw)] || {}) }));
        const selectedTableKeywords = merged.filter((kw: any) => selectedIds.includes(getKeywordKey(kw)));
        
        // Get negative keywords (already stored as objects, simple!)
        const negativeKeywords = negativeKeywordsByCollection?.[clusterId]?.[collectionId] || [];

        // Get selected categories with overrides
        const selectedCategoryIds = selectedCategoryIdsByCollection?.[clusterId]?.[collectionId] || [];
        const categoryBase = baseCategoriesByCluster?.[clusterId] || [];
        const categoryOverrides = categoriesOverridesByCollection?.[clusterId]?.[collectionId] || {};
        const mergedCategories = categoryBase.map((cat) => ({ ...cat, ...(categoryOverrides[getCategoryKey(cat)] || {}) }));
        const selectedTableCategories = mergedCategories.filter((cat) => selectedCategoryIds.includes(getCategoryKey(cat)));
        
        // Derive actual keyword types from selected keywords
        const actualKeywordTypes = [...new Set(
            selectedTableKeywords
                .map((kw: any) => kw.keyword_type)
                .filter((type: string) => type && type !== 'custom' && type !== 'negative')
        )];
        
        let updatedCohorts;
        
        if (editingCohortId) {
            // Update existing cohort
            updatedCohorts = existingCohorts.map((cohort: IKeywordCohort) => {
                if (cohort.id === editingCohortId) {
                    return {
                        ...cohort,
                        keywordTypes: actualKeywordTypes,
                        keywords: selectedTableKeywords,
                        negativeKeywords: negativeKeywords,
                        categories: selectedTableCategories,
                        updatedAt: new Date().toISOString(),
                    };
                }
                return cohort;
            });
        } else {
            // Create new cohort
            const newCohort: IKeywordCohort = {
                id: `cohort_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                keywordTypes: actualKeywordTypes,
                keywords: selectedTableKeywords,
                negativeKeywords: negativeKeywords,
                categories: selectedTableCategories,
                createdAt: new Date().toISOString(),
            };
            
            // Add to existing cohorts
            updatedCohorts = [...existingCohorts, newCohort];
        }
        
        // Merge all cohort keywords for the collection level
        const allKeywords = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.keywords || []);
        const allNegativeKeywords = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.negativeKeywords || []);
        const allCategories = updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.categories || []);
        const allKeywordTypes = [...new Set(updatedCohorts.flatMap((cohort: IKeywordCohort) => cohort.keywordTypes || []))];
        
        // Get current collection to regenerate campaigns
        const citiesRaw = currentCollection?.city || [];
        const cities: string[] = Array.isArray(citiesRaw) ? citiesRaw : (citiesRaw ? [citiesRaw] : []);
        const campaignType = currentCollection?.campaignType;
        const collectionType = currentCollection?.type;
        
        // Regenerate campaigns based on updated cohorts
        const updatedCampaigns = generateCampaignsFromCohorts(
            updatedCohorts,
            cities,
            collectionType || '',
            campaignType || '',
            currentCluster?.name || '',
            clusterId,
            collectionId
        );
        
        // Update collection with cohorts, merged data, and regenerated campaigns
        dispatch(
            updateCollection({
                clusterId,
                collectionId,
                updates: {
                    keywordCohorts: updatedCohorts,
                    keywords: allKeywords,
                    negative_keywords: allNegativeKeywords,
                    categories: allCategories,
                    keywordType: allKeywordTypes,
                    ...(updatedCampaigns.length > 0 && { campaigns: updatedCampaigns }),
                }
            })
        );
        
        // Clear selections for this collection so next modal open is fresh
        setSelectedKeywordIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            delete clusterMap[collectionId];
            return { ...prev, [clusterId]: clusterMap };
        });
        setOverridesByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            delete clusterMap[collectionId];
            return { ...prev, [clusterId]: clusterMap };
        });
        setNegativeKeywordsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            delete clusterMap[collectionId];
            return { ...prev, [clusterId]: clusterMap };
        });
        setSelectedCategoryIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            delete clusterMap[collectionId];
            return { ...prev, [clusterId]: clusterMap };
        });
        setCategoriesOverridesByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            delete clusterMap[collectionId];
            return { ...prev, [clusterId]: clusterMap };
        });
        
        setIsModalVisible(false);
    };

    const handleCampaignCollectionBreakdown = (clusterId: string, collectionId: string, setIsRowExpanded: (expanded: boolean) => void, isRowExpanded: boolean) => {
        setIsRowExpanded(!isRowExpanded);
        const collection = clusters.find((cluster: any) => cluster.id === clusterId)?.collections.find((collection: any) => collection.id === collectionId);
    };

    const handleCreateNew = () => {
        setIsModalVisible(true);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
        const q = value.trim().toLowerCase();
        // clusters search - filter out product clusters (ones with id starting with '##productCluster_')
        const realClusters = clusters.filter((cluster: any) => !cluster.id.startsWith('##productCluster_'));
        setFilteredClusters(
            q === ""
                ? realClusters
                : realClusters.filter(
                      (cluster: any) =>
                          cluster.name.toLowerCase().includes(q)
                  )
        );
        // products search
        setFilteredProducts(
            q === ""
                ? productGroups
                : productGroups.filter(
                      (p) =>
                          p.title.toLowerCase().includes(q) ||
                          (p.sku && p.sku.toLowerCase().includes(q))
                  )
        );
    };

    const handleToggleExpand = (clusterId: string) => {
        if (expandedClusters.includes(clusterId)) {
            setExpandedClusters(
                expandedClusters.filter((id) => id !== clusterId)
            );
        } else {
            setExpandedClusters([...expandedClusters, clusterId]);
        }
    };

    const createCampaignsHandler = async (action: CREATE_CAMPAIGN_HANDLER_ACTIONS) => {
        // Handle back/next navigation
        if (action === CREATE_CAMPAIGN_HANDLER_ACTIONS.NEXT) {
          if (currentStep >= 2) return;
          setCurrentStep(currentStep + 1);
          return;
        }
        if (action === CREATE_CAMPAIGN_HANDLER_ACTIONS.BACK) {
          if (currentStep <= 0) return;
          setCurrentStep(currentStep - 1);
          return;
        }
      
        if (action === CREATE_CAMPAIGN_HANDLER_ACTIONS.PUBLISH) {
          // stay on currentStep (likely 2) unless you want to change after success
      
          const allClusters = store.getState().clusters?.clusters;
          if (!Array.isArray(allClusters)) {
            showToast("danger", "No clusters available to publish.");
            return;
          }
      
          // Include both regular selected clusters and product clusters
          const activeClusters = allClusters.filter((c: any) => {
            // Regular selected clusters
            if (selectedClusters.includes(c.id)) return true;

            // Product clusters (check if the product ID is selected)
            if (c.id.startsWith('##productCluster_')) {
              const productId = c.id.replace('##productCluster_', '');
              return selectedProductIds.includes(productId);
            }

            return false;
          });

          if (activeClusters.length === 0) {
            showToast("danger", "Please select at least one cluster to publish.");
            return;
          }
      
          try {
            setIsPublishingCampaigns(true);
            const campaignsPayload = publishBulkCampaigns(activeClusters);
            if (!Array.isArray(campaignsPayload?.campaigns) || campaignsPayload.campaigns.length === 0) {
              showToast("danger", "No valid campaigns to publish.");
              setIsPublishingCampaigns(false);
              return;
            }

            const response = await createBulkCampaigns(campaignsPayload);
            if (!response.status) {
                throw new Error(response.message);
            }
            showToast("success", response.message);
            store.dispatch(updateCampaignCreationDisplay(false));
          } catch (err:any) {
            showToast("danger", err.message);
          } finally {
            setIsPublishingCampaigns(false);
          }
        }
      };

    // Derive current modal's keywords/selection for backward compatibility
    const currentKeywords = useMemo(() => {
        const { clusterId, collectionId, selectedKwTypes } = modalParams || {};
        if (!clusterId || !collectionId) return [];
        const base = baseKeywordsByCluster?.[clusterId] || [];
        const overrides = overridesByCollection?.[clusterId]?.[collectionId] || {};
        
        // Merge base with overrides per keyword and attach stable key and ids for downstream components
        const mergedKeywords = base.map((kw: any) => ({
            ...kw,
            ...(overrides[getKeywordKey(kw)] || {}),
            key: getKeywordKey(kw),
            clusterId,
            id: collectionId,
        }));
        
        // Filter by selected keyword types from the modal checkboxes
        // Always show custom keywords regardless of filter
        if (selectedKwTypes && selectedKwTypes.length > 0) {
            return mergedKeywords.filter((kw: any) => 
                kw.keyword_type === KeywordTypeValues.CUSTOM || kw.isCustom || selectedKwTypes.includes(kw.keyword_type)
            );
        }
        
        return mergedKeywords;
    }, [modalParams, baseKeywordsByCluster, overridesByCollection, getKeywordKey]);

    // Derive current modal's categories
    const currentCategories = useMemo(() => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return [];
        const base = baseCategoriesByCluster?.[clusterId] || [];
        const overrides = categoriesOverridesByCollection?.[clusterId]?.[collectionId] || {};
        // Merge base with overrides per category and attach stable key and ids for downstream components
        return base.map((cat: any) => ({
            ...cat,
            ...(overrides[getCategoryKey(cat)] || {}),
            key: getCategoryKey(cat),
            clusterId,
            id: collectionId,
        }));
    }, [modalParams, baseCategoriesByCluster, categoriesOverridesByCollection, getCategoryKey]);

    const currentSelectedKeywords = useMemo(() => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return [];
        
        // Get selected table keywords
        // IMPORTANT: Use unfiltered merged keywords to preserve selections even when keyword types are filtered out
        const ids = selectedKeywordIdsByCollection?.[clusterId]?.[collectionId] || [];
        const base = baseKeywordsByCluster?.[clusterId] || [];
        const overrides = overridesByCollection?.[clusterId]?.[collectionId] || {};
        
        // Merge base with overrides (unfiltered by selectedKwTypes) to preserve all selected keywords
        const unfilteredMergedKeywords = base.map((kw: IBCCKeyword) => ({
            ...kw,
            ...(overrides[getKeywordKey(kw)] || {}),
            key: getKeywordKey(kw),
            clusterId,
            id: collectionId,
        }));
        
        // Filter selected keywords from unfiltered list to preserve selections when types are unchecked
        const selectedTableKeywords = unfilteredMergedKeywords.filter((kw: IBCCKeyword) => ids.includes(getKeywordKey(kw)));
        
        // Get negative keywords (already stored as objects and not required to be reconstructed)
        const negativeKeywords = negativeKeywordsByCollection?.[clusterId]?.[collectionId] || [];
        const negativeKeywordsWithMetadata = negativeKeywords.map((kw: INegativeKeywordTarget) => ({
            ...kw,
            key: getKeywordKey({ ...kw, clusterId, id: collectionId }),
            clusterId,
            id: collectionId
        }));
        
        // Combine both types
        return [...selectedTableKeywords, ...negativeKeywordsWithMetadata];
    }, [modalParams, selectedKeywordIdsByCollection, negativeKeywordsByCollection, baseKeywordsByCluster, overridesByCollection, getKeywordKey]);

    const currentSelectedCategories = useMemo(() => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return [];
        
        // Get selected table categories (filter from base list)
        const ids = selectedCategoryIdsByCollection?.[clusterId]?.[collectionId] || [];
        const selectedTableCategories = currentCategories.filter((cat: ICategoryTarget) => ids.includes(getCategoryKey(cat)));
        
        return selectedTableCategories;
    }, [modalParams, selectedCategoryIdsByCollection, currentCategories, getCategoryKey]);

    // Helper to update overrides for a collection from a full rows array
    const applyKeywordsForCollection = useCallback((clusterId: string, collectionId: string, rows: any[]) => {
        // Convert full rows into minimal overrides vs base to avoid storing large arrays
        const base = baseKeywordsByCluster?.[clusterId] || [];
        const baseByKey: Record<string, any> = {};
        base.forEach((kw: any) => { baseByKey[getKeywordKey(kw)] = kw; });
        const overrides: Record<string, any> = {};
        (rows || []).forEach((kw: any) => {
            const key = getKeywordKey(kw);
            const baseKw = baseByKey[key] || {};
            const diff: any = {};
            Object.keys(kw || {}).forEach((k) => {
                if (kw[k] !== baseKw[k]) diff[k] = kw[k];
            });
            if (Object.keys(diff).length > 0) overrides[key] = diff;
        });
        setOverridesByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            clusterMap[collectionId] = overrides;
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [baseKeywordsByCluster, getKeywordKey]);

    // Exposed setters for context (defined above for clarity)
    const setKeywordsForCurrent = useCallback((rows: any[]) => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;
        applyKeywordsForCollection(clusterId, collectionId, rows);
    }, [modalParams, applyKeywordsForCollection]);

    const setSelectedKeywordsForCurrent = useCallback((rows: any[]) => {
        const { clusterId, collectionId } = modalParams || {};
        if (!clusterId || !collectionId) return;
        setSelectedKeywordIdsByCollection((prev) => {
            const clusterMap = prev[clusterId] ? { ...prev[clusterId] } : {};
            clusterMap[collectionId] = (rows || []).map((r: any) => getKeywordKey(r));
            return { ...prev, [clusterId]: clusterMap };
        });
    }, [modalParams, getKeywordKey]);

    const isNextStepDisabled = useCallback((config?: {
        clusterCount?: number;
        productCount?: number;
    }, stepToCheck?: number) : boolean => {
        // Use provided step or default to currentStep
        const step = stepToCheck !== undefined ? stepToCheck : currentStep;
        
        switch(step) {
            case 0:
                // !NOTE: implement the logic to check if all products are disabled in a cluster
                return !((config?.clusterCount ?? 0) || (config?.productCount ?? 0));
            case 1:{
                // !NOTE[DND]: 
                // Step 1: Collections - disable if:
                // 0) Cluster has empty budget (totalBudget) or no start date (startDate)
                // 1) Any collection has empty required fields (campaignType, keywordType, city, type, keywords, budget)
                // 2) Combinations have empty budget or no budget
                // 3) There is no campaign combination available

                const allClusters = store.getState().clusters?.clusters;
                if (!Array.isArray(allClusters)) return true;

                // Get active clusters (both selected clusters and product clusters)
                const activeClusters = allClusters.filter((c: any) => {
                    if (selectedClusters.includes(c.id)) return true;
                    if (c.id.startsWith('##productCluster_')) {
                        const productId = c.id.replace('##productCluster_', '');
                        return selectedProductIds.includes(productId);
                    }
                    return false;
                });

                if (activeClusters.length === 0) return true;

                for (const cluster of activeClusters) {
                    // Check cluster-level required fields first
                    const clusterBudget = cluster.totalBudget;
                    const clusterStartDate = cluster.startDate;
                    
                    // Validate cluster budget and start date
                    if (!clusterBudget || clusterBudget === '' || Number(clusterBudget) <= 0 || !clusterStartDate) {
                        return true;
                    }

                    const collections = cluster.collections || [];
                    
                    if (collections.length === 0) return true;

                    for (const collection of collections) {
                        const campaignType = collection.campaignType;
                        const keywordType = Array.isArray(collection.keywordType) ? collection.keywordType : (collection.keywordType ? [collection.keywordType] : []);
                        const city = Array.isArray(collection.city) ? collection.city : (collection.city ? [collection.city] : []);
                        const type = collection.type;
                        const keywords = collection.keywords || [];
                        const negativeKeywords = collection.negative_keywords || [];
                        const categories = collection.categories || [];
                        const totalTargets = keywords.length + negativeKeywords.length + categories.length;
                        const budget = collection.budget;
                        
                        // 1) Check if any required field is empty (including keywords/categories and budget)
                        // Note: At least one target (keyword, negative keyword, or category) is required
                        if (!campaignType || keywordType.length === 0 || city.length === 0 || !type || totalTargets === 0 || !budget || budget === '' || Number(budget) <= 0) {
                            return true;
                        }

                        // 3) Check if there are no campaign combinations available
                        const campaigns = collection.campaigns || [];
                        if (campaigns.length === 0) {
                            return true;
                        }

                        // 2) Check if combinations have empty budget or no budget (skip archived campaigns)
                        const hasInvalidBudget = campaigns.some((campaign: any) => {
                            // Skip validation for archived/disabled campaigns
                            if (campaign.isArchived) return false;
                            return !campaign.budget || campaign.budget === '' || Number(campaign.budget) < minCampaignBudget;
                        });

                        if (hasInvalidBudget) {
                            return true;
                        }
                    }
                }

                return false;                
            }
            default:
                return false;
         }
    }, [currentStep, selectedClusters, selectedProductIds, minCampaignBudget]);

    // Check if a specific step can be navigated to
    const isStepAccessible = useCallback((targetStep: number): boolean => {
        // Step 0 is always accessible
        if (targetStep === 0) return true;
        
        // Can always navigate to current step or previous steps
        if (targetStep <= currentStep) return true;
        
        // For steps ahead of current, check if all intermediate steps are valid
        // We need to validate all steps from currentStep to targetStep - 1
        // isNextStepDisabled returns true if disabled, so we invert it to check if valid
        for (let step = currentStep; step < targetStep; step++) {
            // For step 0, pass cluster/product counts
            const config = step === 0 ? {
                clusterCount: selectedClusters.length,
                productCount: selectedProductIds.length
            } : undefined;
            
            // isNextStepDisabled returns true if disabled, so if it returns true, step is not accessible
            if (isNextStepDisabled(config, step)) {
                return false;
            }
        }
        
        return true;
    }, [currentStep, isNextStepDisabled, selectedClusters, selectedProductIds]);

    const currentAssets: IBCCAsset[] = [{
        name: "Re-targeting",
        desc: "Boost repeat  purchases by highlighting previously bought products",
        exact_suggested_min: 100,
        exact_suggested_max: 200,
        cpm_bid: null,
    },
    {
        name: "New User Targeting",
        desc: "Attract new customers by promoting based on their past picks",
        exact_suggested_min: 100,
        exact_suggested_max: 200,
        cpm_bid: null,
    },
    {
        name: "Cart Recommendations",
        desc: "Assist cross-selling by showcasing products that pair well with cart items",
        exact_suggested_min: 100,
        exact_suggested_max: 200,
        cpm_bid: null,
    },
    {
        name: "Next Product Recommendations",
        desc: "Maximize cross-sell with instant suggestions for complementary products",
        exact_suggested_min: 100,
        exact_suggested_max: 200,
        cpm_bid: null,
    },
    {
        name: "Similar Product Recommendations",
        desc: "Increase product visibility by featuring your products on related product pages",
        exact_suggested_min: 100,
        exact_suggested_max: 200,
        cpm_bid: null,
    },
    ];

    const contextValues: ICampaignCreationContext = {
        clusters,
        activeTab,
        setActiveTab,
        searchValue,
        setSearchValue,
        filteredClusters,
        setFilteredClusters,
        productGroups,
        setProductGroups,
        // expose per-modal computed values to avoid breaking consumers
        keywords: currentKeywords,
        setKeywords: setKeywordsForCurrent,
        targetCategories: currentCategories,
        selectedTargetCategories: currentSelectedCategories,
        filteredProducts,
        setFilteredProducts,
        expandedClusters,
        setExpandedClusters,
        currentStep,
        setCurrentStep,
        isLoadingProducts,
        setIsLoadingProducts,
        isSavingCluster,
        isPublishingCampaigns,
        selectedProducts,
        setSelectedProducts,
        selectedKeywords: currentSelectedKeywords,
        setSelectedKeywords: setSelectedKeywordsForCurrent,
        clusterName,
        setClusterName,
        isModalVisible,
        setIsModalVisible,
        selectedClusters,
        setSelectedClusters,
        selectedProductIds,
        setSelectedProductIds,
        stepMeta,
        activeStep,
        handleModalCancel,
        handleProductSelection,
        handleKeywordSelection,
        handleCategorySelection,
        handleNegativeKeywordSelection,
        handleClusterSelection,
        handleClusterDeselection,
        handleProductSelectionInLeftPanel,
        handleProductDeselection,
        handleSaveCluster,
        handleFieldChange,
        handleCampaignNameChange,
        handleAddKeywords,
        handleEditCohort,
        handleAddCustomKeyword,
        handleSaveKeywords,
        handleCampaignCollectionBreakdown,
        handleCreateNew,
        createCampaignsHandler,
        handleToggleExpand,
        modalParams,
        setModalParams,
        handleSearch,
        citiesList,
        setCitiesList,
        isNextStepDisabled,
        isStepAccessible,
        handleStepNavigation: (targetStep: number) => {
            if (isStepAccessible(targetStep)) {
                setCurrentStep(targetStep);
            }
        },
        fetchClusterTargets,
        loadingClustersTargets,
        handleAddAssets,
        modalType,
        setModalType,
        assets: currentAssets,
        handleBatchKeywordFieldUpdates,
        handleBatchCategoryFieldUpdates
    };

    return (
        <>
            <campaignCreationContext.Provider value={contextValues}>
                {props.children}
            </campaignCreationContext.Provider>
        </>
    );
});

export default CreateCampaignContext;